'use client';

import { useState } from 'react';
import { X, Pencil, Trash2, Copy } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import { useSkillPrefs } from '..';
import { useSkillsStore } from '../store';
import { SkillContextToggle } from './SkillContextToggle';
import { SkillSettingsForm } from './SkillSettingsForm';
import { SkillUploadDialog } from './SkillUploadDialog';

interface SkillDetailPanelProps {
  skillId: string;
  onClose: () => void;
}

export function SkillDetailPanel({ skillId, onClose }: SkillDetailPanelProps) {
  const t = useTranslations('skills');
  const { skill, toggleContext, updateSetting } = useSkillPrefs(skillId);
  const deleteSkill = useSkillsStore((s) => s.actions.deleteSkill);
  const duplicateSkill = useSkillsStore((s) => s.actions.duplicateSkill);
  const selectSkill = useSkillsStore((s) => s.actions.selectSkill);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  if (!skill) return null;

  // Sort by context name so the toggle order is stable. The backend serializes
  // `contexts` from a Go map, whose iteration order is randomized, so without
  // this the list reshuffles every time a skill is (re)fetched.
  const contextEntries = Object.entries(skill.contexts ?? {}).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  const isSystem = skill.source === 'system';
  const prompt = skill.raw_yaml ?? '';

  const handleDelete = () => {
    if (confirmDelete) {
      void deleteSkill(skill.id);
      selectSkill(null);
    } else {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 3000);
    }
  };

  const handleDuplicate = async () => {
    setDuplicating(true);
    try {
      const newId = await duplicateSkill(skill.id);
      if (newId) selectSkill(newId);
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col h-full border-l border-border bg-surface',
        'w-full md:w-[380px] shrink-0',
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex flex-col gap-0.5 min-w-0">
          <h2 className="font-serif text-base font-medium leading-tight truncate">{skill.name}</h2>
          <span className="text-xs text-muted-foreground capitalize">{skill.category}</span>
        </div>
        <div className="flex items-center gap-0.5">
          {isSystem && (
            <button
              type="button"
              onClick={() => void handleDuplicate()}
              disabled={duplicating || !prompt}
              aria-label={t('skillDetailPanel.duplicateToMySkills')}
              title={t('skillDetailPanel.duplicateToMySkills')}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-hover transition-colors duration-150 disabled:opacity-40 disabled:pointer-events-none"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          )}
          {skill.source === 'user' && (
            <>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                aria-label={t('skillDetailPanel.editSkill')}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-hover transition-colors duration-150"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={handleDelete}
                aria-label={confirmDelete ? t('skillDetailPanel.confirmDelete') : t('skillDetailPanel.deleteSkill')}
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-md transition-colors duration-150',
                  confirmDelete
                    ? 'text-destructive hover:bg-destructive/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-hover',
                )}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('skillDetailPanel.closePanel')}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-hover transition-colors duration-150"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {contextEntries.length > 0 && (
          <section>
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              {t('skillDetailPanel.enabledContexts')}
            </h3>
            <div>
              {/* TODO: Pass estimated_cost_usd and estimated_duration_sec to SkillContextToggle when credit system is implemented */}
              {contextEntries.map(([ctxName, ctx]) => (
                <SkillContextToggle
                  key={ctxName}
                  contextName={ctxName}
                  enabled={ctx.enabled}
                  onToggle={(enabled) => toggleContext(ctxName, enabled)}
                />
              ))}
            </div>
          </section>
        )}

        {(skill.settings?.length ?? 0) > 0 && (
          <section>
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              {t('skillDetailPanel.settings')}
            </h3>
            <SkillSettingsForm
              skillId={skill.id}
              skillName={skill.name}
              settings={skill.settings}
              onUpdate={updateSetting}
            />
          </section>
        )}

        {prompt && (
          <section>
            <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
              {isSystem ? t('skillDetailPanel.definitionReadOnly') : t('skillDetailPanel.definition')}
            </h3>
            <pre className="max-h-80 overflow-auto rounded-md border border-border bg-background p-3 text-xs font-mono whitespace-pre-wrap break-words text-foreground/90">
              {prompt}
            </pre>
            {isSystem && (
              <button
                type="button"
                onClick={() => void handleDuplicate()}
                disabled={duplicating}
                className={cn(
                  'mt-3 inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5',
                  'text-xs font-medium text-foreground hover:bg-hover transition-colors duration-150',
                  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
                  'disabled:opacity-50 disabled:pointer-events-none',
                )}
              >
                <Copy className="h-3.5 w-3.5" />
                {duplicating ? t('skillDetailPanel.duplicating') : t('skillDetailPanel.duplicateToMySkills')}
              </button>
            )}
          </section>
        )}
      </div>

      {editOpen && (
        <SkillUploadDialog
          mode="edit"
          skillId={skill.id}
          initialYaml={skill.raw_yaml ?? ''}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  );
}
